import React, { ChangeEvent, Key, useEffect, useState } from 'react'
import './LeaveApplication.scss'
import { BreadCrumb } from 'Components'
import { Controller, useForm } from 'react-hook-form'
import { leaveService } from '../../../../../Services/leaveService'
import Swal from 'sweetalert2'
import { useNavigate, useParams } from 'react-router-dom'
import profileService from 'Services/profileService'
import { EmployeeType } from 'Models'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import employeeService from 'Services/employeeService'
import { TimeZone } from 'Helpers'
import { ILeaveRemainingDaysType } from 'Models/leaveType'
import { CloudUploadSvg } from 'Components/Svgs'
import uploadDocumentService from 'Services/uploadDocumentService'
import Select from 'react-select'

export type leaveFormType = {
  startDate: Date | string | undefined | null
  endDate: Date | string | undefined | null
  leaveType: string | undefined
  days: number
  reason: string
  handoverNote: string
  relieverEmailAddress?: string
}

const LeaveApplication = () => {
  const [request, setRequest] = useState(false)
  const navigate = useNavigate()
  const { leaveType, days: paramDays } = useParams<{
    leaveType: string | undefined
    days: string | undefined
  }>()

  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [maxDate, setMaxDate] = useState<Date>(new Date())
  const [maxEndDate, setMaxEndDate] = useState<Date>(new Date())

  const [minStartDate, setMinStartDate] = useState<Date | undefined>(new Date())

  const [minDate, setMinDate] = useState<Date | undefined>(new Date())

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<leaveFormType>()

  const submit = async (data: leaveFormType) => {
    setRequest(true)
    // console.log({ startDate: TimeZone(startDate) })
    // console.log({ endDate: TimeZone(endDate) })
    await leaveService
      .create({
        ...data,
        startDate: TimeZone(startDate),
        endDate: TimeZone(endDate),
        leaveType,
      })
      .then(
        async (res: any) => {
          setRequest(false)
          Swal.fire({
            icon: 'success',
            title: 'Congratulations!',
            text: 'Your leave application has been submitted successfully',
            showConfirmButton: false,
            timer: 3000,
          })
          const leaveId = res?.data?.data?.id

          if (selectedFile && leaveId) {
            await uploadFile(selectedFile, leaveId)
          }
          setTimeout(() => {
            navigate('/leave')
          }, 2000)
        },
        (error) => {
          setRequest(false)
          Swal.fire({
            icon: 'warning',
            title: 'Oops!',
            text: error.response?.data?.error || 'Error submitting application',
            showConfirmButton: false,
            timer: 3000,
          })
        },
      )
  }

  const [profile, setProfile] = useState<EmployeeType>()

  const getProfile = async () => {
    try {
      const response = await profileService.profile()
      // console.log('employee profile', response.data.data)
      setProfile(response.data.data)
    } catch (error) {
      console.log('Error fetching profile:', error)
    }
  }

  useEffect(() => {
    getProfile()
  }, [])

  const isWeekday = (date: Date) => {
    const day = date.getDay()
    return day !== 0 && day !== 6 // Exclude Sundays (0) and Saturdays (6)
  }

  const countWeekdays = (startDate: Date | undefined, endDate: Date | undefined) => {
    if (!startDate || !endDate) return 0
    let count = 0
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      if (isWeekday(currentDate)) {
        count++
      }
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return count
  }

  useEffect(() => {
    if (startDate && endDate) {
      const weekdays = countWeekdays(startDate, endDate)
      setValue('days', weekdays)
    }
  }, [startDate, endDate])

  // set minimum start date on in 7days
  useEffect(() => {
    const today = new Date()
    let count = 0
    const date = new Date(today)

    while (count < 1) {
      date.setDate(date.getDate() + 1)
      if (isWeekday(date)) {
        count++
      }
    }
    setMinStartDate(date)
  }, [])

  useEffect(() => {
    if (profile?.createdAt && leaveType === 'ANNUAL' && maxEndDate) {
      const createdAtDate = new Date(profile.createdAt)
      const currentDate = new Date(startDate || '')
      const oneYearDifference = currentDate.getFullYear() - createdAtDate.getFullYear()

      // Only run if one year hasn't passed and leave type is annual leave
      if (oneYearDifference < 1) {
        const updatedEndDate = new Date(startDate || '') // Create a copy of maxEndDate
        let addedDays = 0

        // Loop to add 10 weekdays
        // while (addedDays < 10) {
        while (addedDays < Number(paramDays)) {
          updatedEndDate.setDate(updatedEndDate.getDate() + 1)
          const dayOfWeek = updatedEndDate.getDay()
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            // Skip weekends
            addedDays++
          }
        }

        // Update the state only once
        setMaxEndDate(updatedEndDate)
      }
    } else {
      const date = new Date(startDate ?? '')
      let count = 0
      if (paramDays) {
        const intParamDays = Number(paramDays)

        // Loop to add only weekdays
        while (count < intParamDays) {
          // Move to the next day
          date.setDate(date.getDate() + 1)
          if (isWeekday(date)) {
            // Only increment count if it's a weekday
            count++
          }
        }

        setMaxEndDate(date)
      }
    }
  }, [profile, leaveType, startDate])

  const [employees, setEmployees] = useState<EmployeeType[]>()
  const [excludeSelf, _] = useState(true)
  // console.log('employees', employees)

  const getUsers = async () => {
    try {
      const firstResponse = await employeeService.empGetUsers(1, excludeSelf)

      const { dataset, total, pageSize } = firstResponse.data.data
      const totalPages = Math.ceil(total / pageSize)

      const remainingRequests = Array.from({ length: totalPages - 1 }, (_, index) =>
        employeeService.empGetUsers(index + 2, excludeSelf),
      )

      const remainingResponses = await Promise.all(remainingRequests)
      const allUsers = [
        ...dataset,
        ...remainingResponses.flatMap((response) => response.data.data.dataset ?? []),
      ]
      setEmployees(allUsers)
    } catch (error: any) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops!',
        text: error.response.data.error || 'Something went wrong',
        showConfirmButton: false,
        timer: 3000,
      })
    }
  }

  useEffect(() => {
    getUsers()
  }, [])

  useEffect(() => {
    console.log('setStartDate', startDate)
  }, [startDate])

  const [leaveLeftDays, setLeaveLeftDays] = useState<ILeaveRemainingDaysType | null>(null)

  const getLeaveRemainingDays = async () => {
    setRequest(true)
    await leaveService.getLeaveRemainingDays().then(
      (data) => {
        // console.log('user left days', data?.data?.data)
        setLeaveLeftDays(data?.data?.data)
        setRequest(false)
      },
      (error: { response: { data: { error: string } } }) => {
        console.log('user left leaveService', error)
        setRequest(false)
        Swal.fire({
          icon: 'warning',
          title: 'Oops!',
          text: error.response.data.error,
          showConfirmButton: false,
          timer: 3000,
        })
      },
    )
  }

  useEffect(() => {
    getLeaveRemainingDays()
  }, [])

  const leaveTypeValue = leaveType
    ? (leaveLeftDays?.[leaveType as keyof ILeaveRemainingDaysType] ?? null)
    : null
  // console.log('leaveTypeValue', leaveTypeValue)

  const isDisabledLeave: boolean =
    paramDays !== undefined && leaveTypeValue !== null
      ? leaveTypeValue > Number(paramDays) || leaveTypeValue === 0
      : true

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [handOverError, setHandOverError] = useState<string | null>()

  function resetFileInput() {
    const fileInput = document.getElementById('upload_file') as HTMLInputElement
    if (fileInput) {
      fileInput.value = '' // input reset
    }
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setSelectedFile(file)
  }

  const handleCancelFile = () => {
    setSelectedFile(null)
    resetFileInput()
  }

  async function uploadFile(file: File, leaveId: string) {
    if (!file || !leaveId) return

    if (file.size > 5 * 1024 * 1024) {
      setHandOverError('File must be less than 5MB')
      return
    }

    setSelectedFile(file)
    setHandOverError(null)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await uploadDocumentService.upload('HAND_OVER_NOTE_UPLOAD', leaveId, formData)

      if (res.data.status === true) {
        Swal.fire({
          text: 'Hand over note uploaded successfully',
          icon: 'success',
          confirmButtonText: 'Ok',
        })
      } else {
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            cancelButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })
        swalWithBootstrapButtons.fire({
          title: 'Oops!',
          text: 'Something went wrong',
          icon: 'warning',
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: 'close',
          reverseButtons: true,
        })
      }
    } catch (err: any) {
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          cancelButton: 'btn btn-danger',
        },
        buttonsStyling: false,
      })
      swalWithBootstrapButtons.fire({
        title: 'Oops!',
        text: err.response.data.error || err.response.data.message || 'Something went wrong',
        icon: 'warning',
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonText: 'close',
        reverseButtons: true,
      })
    }
  }

  return (
    <div className='LeaveApplication me-md-3'>
      <div className='container'>
        <BreadCrumb title='Leave Application' parent='leave' parentUrl='leave' showTitle={false} />
      </div>
      <div className='container mt-5'>
        <div className='card border-0 px-4 pt-3 mx-md-3'>
          <div className='card-header px-0'>
            <div className='row align-items-center'>
              <div className='col-12 col-md-6 col-lg-6 mb-2 mb-md-0'>Leave Application Form</div>

              <div className='col-12 col-md-6 col-lg-6'>
                <div className='d-flex flex-column flex-sm-row gap-2 justify-content-md-end'>
                  <div className='w-100 w-sm-auto'>
                    <button
                      className='btn btn-upload-photo d-flex align-items-center justify-content-center gap-2 w-100'
                      type='button'
                      onClick={() => document.getElementById('upload_file')?.click()}
                    >
                      <CloudUploadSvg />
                      <span>Upload Handover Note</span>
                    </button>

                    <input
                      type='file'
                      id='upload_file'
                      className='d-none'
                      onChange={handleFileChange}
                    />

                    {selectedFile && (
                      <div className='d-flex align-items-center justify-content-center mt-2'>
                        <span className='small text-muted me-2 text-truncate'>
                          {selectedFile.name}
                        </span>
                        <button
                          type='button'
                          className='btn btn-sm py-0 px-2 cancel'
                          onClick={handleCancelFile}
                        >
                          ✕
                        </button>
                      </div>
                    )}

                    {handOverError && <div className='text-danger mt-2'>{handOverError}</div>}
                  </div>

                  <div className='w-100 w-sm-auto'>
                    <a
                      href='https://docs.google.com/document/create?title=TG Handover Note&share=email@gmail.com'
                      target='_blank'
                      rel='noreferrer'
                      className='w-100'
                    >
                      <button className='btndd btn-handover w-100'>Or Create Handover Note</button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='card-body px-0'>
            <form onSubmit={handleSubmit(submit)}>
              <div className='row'>
                <div className='form-group col-md-6 mt-3'>
                  <label>Start Date</label>
                  <DatePicker
                    {...register('startDate', {
                      required: 'Start date is required',
                    })}
                    autoComplete='0'
                    selected={startDate}
                    onChange={(date: Date | null) => {
                      setStartDate(date || undefined)
                      setValue('startDate', date)
                    }}
                    filterDate={isWeekday}
                    minDate={minStartDate}
                    placeholderText='Select a workday'
                    dateFormat='MM/dd/yyyy'
                    className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                  />
                  {errors.startDate && (
                    <div className='invalid-feedback'>{errors.startDate.message}</div>
                  )}
                </div>

                <div className='form-group col-md-6 mt-3'>
                  <label>End Date</label>
                  <DatePicker
                    {...register('endDate', {
                      required: 'End date is required',
                    })}
                    // showWeekend={false}
                    selected={endDate}
                    required={true}
                    // initValue={new Date()}
                    onChange={(date: Date | null) => {
                      setEndDate(date || undefined)
                      setValue('endDate', date)
                    }}
                    filterDate={isWeekday}
                    minDate={startDate}
                    maxDate={maxEndDate}
                    placeholderText='Select a workday'
                    dateFormat='MM/dd/yyyy'
                    className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                  />
                  {errors.endDate && (
                    <div className='invalid-feedback'>{errors.endDate.message}</div>
                  )}
                </div>

                <div className='form-group col-md-6 mt-3'>
                  <label>Total Number of leave Days</label>
                  {leaveTypeValue !== null && (
                    <input
                      type='number'
                      defaultValue={leaveTypeValue}
                      // type='text'
                      className={`form-control ${errors.days ? 'is-invalid' : ''}`}
                      disabled
                      {...register('days', {
                        required: 'Number of days is required',
                      })}
                    />
                  )}
                  {errors.days && <div className='invalid-feedback'>{errors.days.message}</div>}
                </div>

                <div className='form-group col-md-6 mt-3'>
                  <label>Reason for Leave</label>
                  <input
                    type='text'
                    className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                    {...register('reason', {
                      required: 'Reason for the leave is required',
                    })}
                  />
                  {errors.reason && <div className='invalid-feedback'>{errors.reason.message}</div>}
                </div>

                <div className='form-group col-md-6 mt-3'>
                  <label>Handover Note document Url</label>
                  <input
                    placeholder='Created handover note? Paste link here'
                    type='text'
                    className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                    {...register('handoverNote', {
                      // required: 'Reason for the leave is required',
                    })}
                  />
                  {errors.reason && <div className='invalid-feedback'>{errors.reason.message}</div>}
                </div>

                <div className='form-group col-12 col-md-6 mt-3'>
                  <label htmlFor='relieverEmailAddress'>Reliever</label>

                  <Controller
                    name='relieverEmailAddress'
                    control={control}
                    rules={{
                      required: 'Field is required',
                    }}
                    render={({ field }) => (
                      <Select
                        inputId='relieverEmailAddress'
                        options={employees}
                        isSearchable
                        isMulti={false}
                        placeholder='Search and select reliever...'
                        getOptionValue={(employee) => employee.emailAddress}
                        getOptionLabel={(employee) => `${employee.firstName} ${employee.lastName}`}
                        value={
                          employees?.find((employee) => employee.emailAddress === field.value) ||
                          null
                        }
                        onChange={(employee) => field.onChange(employee?.emailAddress || '')}
                        formatOptionLabel={(employee) => (
                          <div className='employee-option'>
                            <div className='employee-option-name'>
                              {employee.firstName} {employee.lastName}
                            </div>

                            <div className='employee-option-details'>
                              <span>{employee.emailAddress ?? 'nil'}</span>
                              <span>•</span>
                              <span>{employee.department ?? 'nil'}</span>
                              <span>•</span>
                              <span>{employee.jobTitle ?? 'nil'}</span>
                            </div>
                          </div>
                        )}
                        className={errors.relieverEmailAddress ? 'is-invalid' : ''}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '45px',
                            height: '45px',
                            borderRadius: '0.375rem',
                          }),

                          valueContainer: (base) => ({
                            ...base,
                            height: '45px',
                            padding: '0 12px',
                          }),

                          placeholder: (base) => ({
                            ...base,
                            fontSize: '12px',
                          }),

                          input: (base) => ({
                            ...base,
                            fontSize: '14px',
                          }),

                          singleValue: (base) => ({
                            ...base,
                            fontSize: '14px',
                          }),
                        }}
                      />
                    )}
                  />

                  {errors.relieverEmailAddress && (
                    <div className='invalid-feedback d-block'>
                      {errors.relieverEmailAddress.message}
                    </div>
                  )}
                </div>

                <div className='col-md-6'></div>
                <div className='form-group mt-3 col-md-6 text-md-end'>
                  <button
                    type='submit'
                    className='btn btn-default btn-apply mt-4'
                    disabled={request || isDisabledLeave}
                  >
                    {request && <span className='spinner-border spinner-border-sm'></span>}
                    Apply for Leave
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeaveApplication
